import {
    getDemographicCatalog,
    getEquipmentCatalog,
    isEquipmentBased,
    isGiftOfferProduct,
    isMembershipProduct,
} from "common";
import { isNil, omit, sumBy } from "lodash";
import {
    AllowedPrivacy,
    BaseScheduleType,
    CheckInValidationType,
    EquipmentCatalogItem,
    Experience,
    ExperienceGroup,
    ExperienceOption,
    ExperienceStatus,
    GiftPreference,
    GoogleLocationResponse,
    LineItemType,
    MeetingLocation,
    Membership,
    OPEN_UNLIMITED,
    PaymentMethod,
    Product,
    ProductObject,
    ReviewDestination,
    Schedule,
    ScheduleType,
    SelectedExperiences,
    Seller,
    Source,
    UnitType,
    UserDetailsRequirement,
} from "types";
import { getCatalogItemsByUnitType } from "./Catalog";
import { getGiftDetailsRequirements } from "./GiftPreference";
import { isPurchaseItemizationEnabled } from "./Seller";
import { queryClient } from "@/api/queryClient";
import { getValidationByType } from "@/components/Product/ExperienceCheckIn/useCheckInValidationField";

export const getOptions = (experience?: Experience): ExperienceOption[] => experience?.optionsPreference?.options ?? [];

export const getActiveOptions = (experience?: Experience): ExperienceOption[] =>
    getOptions(experience).filter((option) => !option.deletedAt);

export const isOptionsEnabled = (experience?: Experience): boolean => experience?.optionsPreference?.enabled ?? false;

export interface OptionChoice {
    value: string;
    label: string;
}

export const toOptionChoices = (options: ExperienceOption[]): OptionChoice[] =>
    options.map((option) => ({ value: option.id, label: option.name }));

export const getDefaultExperience = (seller: Seller): Omit<Experience, "id"> => {
    return {
        name: "",
        desc: "",
        pickupAddress: "",
        geo: { lat: null, lng: null },
        excerpt: "",
        duration: 1,
        seller: { id: seller.id },
        resources: [],
        currency: "",
        schedules: [],
        medias: [],
        group: getDefaultGroup(),
        catalog: { items: [], terms: [], promotions: [] },
        object: ProductObject.EXPERIENCE,
        earlyReturn: false,
        balanceDueReminder: false,
        autoChargeOnDepositReminder: false,
        balanceDueOffset: 0,
        allowedPrivacies: [AllowedPrivacy.PUBLIC],
        paymentMethod: PaymentMethod.CREDIT_CARD,
        requireCCForSources: [],
        firstCheckinExpirationOffset: null,
        purchaseExpirationOffset: null,
        // If order itemization is enabled, allow experience with no schedules.
        scheduleType: isPurchaseItemizationEnabled(seller) ? ScheduleType.OPEN : undefined,
    };
};

export const hasMappedMeetingLocation = (experience?: Partial<Experience>) => {
    return !!experience?.meetingLocation || (!isNil(experience?.geo?.lat) && !isNil(experience?.geo?.lng));
};

export const applyMapPreferenceToPayload = (
    values: Partial<Experience> & { enableMap?: boolean },
): Partial<Experience> => {
    const payload: Partial<Experience> = omit(values, "enableMap");

    if (values.enableMap || values.virtualMeeting?.enabled) {
        return payload;
    }

    return { ...payload, meetingLocation: null, geo: {} };
};

export const sanitizeExperiencePayload = (payload: Partial<Experience>) => {
    const fieldsToRemove = ["priceSchemes", "addOns", "demographics", "openingHours", "catalog"];

    if (payload.meetingLocation?.geo) {
        fieldsToRemove.push("geo");
    }

    if (payload.meetingLocation?.formattedAddress) {
        fieldsToRemove.push("pickupAddress");
    }

    const sanitized = omit(payload, fieldsToRemove);

    if (!sanitized.meetingLocation?.externalIdentifiers) {
        return sanitized;
    }

    return {
        ...sanitized,
        meetingLocation: {
            ...sanitized.meetingLocation,
            externalIdentifiers: sanitized.meetingLocation.externalIdentifiers.map((identifier) =>
                omit(identifier, "id"),
            ),
        },
    };
};

export const buildMeetingLocationFromPlace = (place: GoogleLocationResponse): MeetingLocation => {
    const postal = place.postal_address;
    const addressLines = postal.addressLines?.length ? postal.addressLines : [postal.streetLines];

    return {
        name: place.name,
        formattedAddress: place.formatted_address,
        geo: {
            lat: place.location.latitude,
            lng: place.location.longitude,
        },
        postalAddress: {
            streetLines: [...new Set([place.name, ...addressLines].filter(Boolean))].join(", "),
            locality: postal.locality,
            region: postal.administrativeArea ?? postal.region,
            postalCode: postal.postalCode,
            countryCode: postal.regionCode ?? postal.countryCode,
        },
        externalIdentifiers: [{ remoteId: place.place_id, remoteSystem: "google_places" }],
    };
};

export const getDefaultPrice = (experience: Experience | Membership) => {
    if (isMembershipProduct(experience)) {
        return experience.price;
    }

    let defaultPrice;

    const catalogItems = isEquipmentBased(experience)
        ? getEquipmentCatalog(experience)
        : getDemographicCatalog(experience);
    for (const catalogItem of catalogItems) {
        if (catalogItem?.prices?.price?.min) {
            const minPrice = catalogItem.prices.price.min;
            defaultPrice = defaultPrice && defaultPrice < minPrice ? defaultPrice : minPrice;
        }
    }

    return defaultPrice ?? 0;
};

export const getDefaultGroup = (): ExperienceGroup => {
    return {
        orderMin: 1,
        outingMin: 1,
        outingMinCutoff: 3600,
    };
};

export const hasPrivateOption = (experience: Experience): boolean => {
    return experience.allowedPrivacies?.includes(AllowedPrivacy.PRIVATE) ?? false;
};

export const hasPublicOption = (experience: Experience): boolean => {
    return experience.allowedPrivacies?.includes(AllowedPrivacy.PUBLIC) ?? false;
};

export const isAlwaysPublicExperience = (experience: Experience) => {
    return experience.allowedPrivacies?.length === 1 && experience.allowedPrivacies[0] === AllowedPrivacy.PUBLIC;
};

export const isAlwaysPrivateExperience = (experience: Experience) => {
    return experience.allowedPrivacies?.length === 1 && experience.allowedPrivacies[0] === AllowedPrivacy.PRIVATE;
};

export const isOptionallyPrivateExperience = (experience: Experience) => {
    return experience.allowedPrivacies.length > 1 && experience.allowedPrivacies?.includes(AllowedPrivacy.PRIVATE);
};

export const isGratuityForExperienceEnabled = (seller: Seller, experienceId: ID): boolean => {
    const experiences: SelectedExperiences = seller.preferences?.gratuity?.experiences;
    return !!(experiences?.all || experiences?.experiences?.includes(experienceId));
};

export const isScheduled = (experience: Experience) => {
    return experience.scheduleType ? experience.scheduleType !== ScheduleType.OPEN : experience.isScheduled;
};

export const isUnScheduled = (experience: Experience) => !isScheduled(experience);

export const requiresTimeslot = (experience: Experience) => {
    return experience?.schedules.some((schedule) => !!schedule.times?.length);
};

export const hasTimeScheduled = (experience: Experience) => {
    if (experience.schedules.length > 0) {
        return experience.schedules.some((schedule) => schedule.times && schedule.times.length > 0);
    }

    return experience.scheduleType ? experience.scheduleType === ScheduleType.TIME : false;
};

export const getExperienceMin = (experience: Experience) => {
    const { orderMin = 1 } = experience.group ?? {};
    return orderMin;
};

export const getExperienceMax = (experience?: Experience) => {
    return experience?.group?.outingMax ?? OPEN_UNLIMITED - 1;
};

export const getDemographicById = (experience: Experience, id?: ID) => {
    return experience.catalog?.items.find((catalog) => catalog.template?.id === id);
};

export const getReviewPreferenceOptions = () => {
    return [
        ReviewDestination.OFF,
        ReviewDestination.XOLA,
        ReviewDestination.GOOGLE,
        ReviewDestination.TRIPADVISOR,
        ReviewDestination.YELP,
        ReviewDestination.CUSTOM,
    ];
};

export const isCheckInAllowed = (seller: Seller, experience?: Experience) => {
    const defaultValue = seller.preferences.checkIn.enabled ?? true;
    return experience?.checkInPreference?.enabled ?? defaultValue;
};

export const isMultiCheckInAllowed = (seller: Seller, experience?: Experience) => {
    if (!isCheckInAllowed(seller, experience)) {
        return false;
    }

    const checkInPreference = experience?.checkInPreference ?? seller.preferences.checkIn;
    const countValidation = getValidationByType(CheckInValidationType.COUNT, checkInPreference.validations);

    return countValidation ? countValidation.max > 1 : true;
};

export const allowMultipleEventsPerTimeslot = (seller: Seller, experience: Experience) => {
    return seller.preferences.booking.allowMultiplePrivateEvents && experience.resources.length > 0;
};

export const isExperiencePublished = (experience: Experience) => {
    return experience.status === ExperienceStatus.PUBLISHED;
};

export const isVirtualExperience = (experience?: Experience) => {
    return experience?.virtualMeeting?.enabled ?? false;
};

export const isAutoAcceptEnabledForExperience = (experience: Experience, seller: Seller) => {
    const enabledForExperience = seller.autoAccept.experiences.find((experienceId) => experienceId === experience.id);

    if (!seller.autoAccept.all && enabledForExperience) {
        return true;
    }

    return false;
};

export const getCustomerNamePreference = (
    product: Product | Experience,
    giftPreference?: GiftPreference,
): UserDetailsRequirement => {
    if (isGiftOfferProduct(product)) {
        return getGiftDetailsRequirements(giftPreference).organizerDetailsRequirement;
    }

    return product.customerNamePreference ?? UserDetailsRequirement.REQUIRED;
};

export const showCustomerFields = (experience: Experience) => {
    return getCustomerNamePreference(experience) !== UserDetailsRequirement.NOT_COLLECTED;
};

export const isCustomerNameRequired = (preference?: UserDetailsRequirement | null) => {
    return (
        preference === UserDetailsRequirement.REQUIRED || preference === UserDetailsRequirement.OPTIONAL_CONFIRMATION
    );
};

export const isCreditCardRequired = (experience?: Experience) => {
    return experience?.requireCCForSources?.includes(Source.OFFICE);
};

export const canOverrideFutureCutoff = (experience: Experience): boolean => {
    return !!experience.futureCutoff?.allowOverride;
};

export const getSelectedExperiencesByIds = (selectedExperienceIds: ID[], experiences: Experience[]) => {
    if (experiences.length > 0) {
        const selectedExperiences: Experience[] = [];
        for (const selectedExperienceId of selectedExperienceIds) {
            const experience = experiences.find((experience) => experience.id === selectedExperienceId);
            if (experience) {
                selectedExperiences.push(experience);
            }
        }

        return selectedExperiences;
    }

    return [];
};

export const getAvailableSchedules = (experience: Experience | { schedules: Schedule[] }) => {
    if (experience.schedules?.length > 0) {
        return experience.schedules.filter((schedule) => schedule.type === BaseScheduleType.AVAILABLE);
    }

    return [];
};

export const getExperienceFromQueryCache = (experienceId: ID) => {
    return queryClient.getQueryData<Experience>(["products", experienceId]);
};

export const hasUniqueGuestIdentification = (experience?: Experience | Membership) => {
    // Equipment experiences with "Collect demographic information" off have no per-attendee demographic
    // items, so there are no individual attendees to uniquely identify. Treat them the same as any
    // experience with "uniquely identify each attendee" off — questionnaires/waivers are collected at
    // the organizer (item) level.
    if (
        experience &&
        isEquipmentBased(experience as Experience) &&
        !hasSecondaryDemographics(experience as Experience)
    ) {
        return false;
    }

    return experience?.requireGuestIdentification ?? true;
};

export const hasSecondaryDemographics = (experience: Experience): boolean => {
    return getCatalogItemsByUnitType(experience.catalog?.items ?? [], UnitType.DEMOGRAPHIC_SECONDARY).length > 0;
};

/**
 * How many attendees the booked equipment can seat: each unit's catalog capacity times how many of it
 * are booked. Driven off the catalog so a line item with no catalog entry contributes nothing.
 */
export const getMaxAttendees = (
    experience: Experience,
    lineItems: Array<{ type?: string; template?: { id: ID }; quantity?: number | null }> = [],
) => {
    const equipmentCatalogItems = getCatalogItemsByUnitType<EquipmentCatalogItem>(
        experience.catalog?.items ?? [],
        UnitType.EQUIPMENT,
    );

    return sumBy(equipmentCatalogItems, (catalogItem) => {
        const quantity =
            lineItems.find(
                (lineItem) =>
                    (lineItem.type === undefined || lineItem.type === LineItemType.EQUIPMENT) &&
                    lineItem.template?.id === catalogItem.template?.id,
            )?.quantity ?? 0;

        return quantity * (catalogItem.capacity ?? 1);
    });
};
